import { lazy, Suspense, useEffect, useState } from "react";
import "./waiting-room.css";

export type PatrolPhase =
  | "entry"
  | "clock"
  | "under-bed"
  | "detector"
  | "hello";

type InspectionPoint = Extract<
  PatrolPhase,
  "clock" | "under-bed" | "detector"
>;

type InspectionDefinition = {
  id: InspectionPoint;
  code: string;
  shortTitle: string;
  title: string;
  hint: string;
  description: string;
};

const RoomScene3D = lazy(() =>
  import("./RoomScene3D").then((module) => ({ default: module.RoomScene3D })),
);

const INSPECTION_POINTS: readonly InspectionDefinition[] = [
  {
    id: "clock",
    code: "A1",
    shortTitle: "Réveil",
    title: "Réveil & chargeurs",
    hint: "Table de nuit",
    description:
      "Regarder les objets orientés vers le lit, leurs ouvertures et leurs voyants inhabituels.",
  },
  {
    id: "under-bed",
    code: "A2",
    shortTitle: "Sous le lit",
    title: "Dessous & mobilier",
    hint: "Angle bas",
    description:
      "Changer de hauteur révèle des objets et des câbles invisibles depuis l’entrée de la pièce.",
  },
  {
    id: "detector",
    code: "A3",
    shortTitle: "En hauteur",
    title: "Détecteur & plafond",
    hint: "Angle haut",
    description:
      "Vérifier les objets fixes placés au-dessus du regard, sans les démonter ni les manipuler.",
  },
] as const;

const OVERVIEW = {
  code: "360°",
  title: "Vue d’ensemble",
  description:
    "Glissez dans toutes les directions, puis pincez ou utilisez la molette pour ajuster la distance.",
};

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function SceneFallback() {
  return (
    <div className="wr-scene-suspense" aria-live="polite">
      <span aria-hidden="true" />
      <p>La chambre se met en place…</p>
    </div>
  );
}

const MAILTO =
  "mailto:san@data-san.com?subject=Je%20veux%20tester%20les%20applications%20desktop%20et%20iPhone%20de%20Is%20There%20A%20Camera";

export function WaitingRoom() {
  const [phase, setPhase] = useState<PatrolPhase>("entry");
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === "undefined" || !document.hidden,
  );
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);

  const activePoint = INSPECTION_POINTS.find((point) => point.id === phase);
  const sceneReadout = activePoint ?? OVERVIEW;

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    const previousDescription = description?.content;

    document.body.classList.add("waiting-room-active");
    document.title = "ITAC — Is There A Camera · Vérifiez la pièce";
    if (description) {
      description.content =
        "Is There A Camera réunit une application desktop d’analyse réseau et une application iPhone d’inspection guidée des lieux où vous dormez.";
    }

    return () => {
      document.body.classList.remove("waiting-room-active");
      document.title = previousTitle;
      if (description && previousDescription) {
        description.content = previousDescription;
      }
    };
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => setReducedMotion(media.matches);
    const updateVisibility = () => setPageVisible(!document.hidden);

    media.addEventListener("change", updateMotionPreference);
    document.addEventListener("visibilitychange", updateVisibility);

    return () => {
      media.removeEventListener("change", updateMotionPreference);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, []);

  return (
    <main className="wr-page" id="main-content">
      <a className="wr-skip-link" href="#waiting-room-title">
        Aller au contenu
      </a>

      <header className="wr-header">
        <a
          className="wr-brand"
          href={import.meta.env.BASE_URL === "./" ? "./" : "/waiting-room"}
          aria-label="ITAC — Is There A Camera, accueil">
          <span className="wr-brand-monogram" aria-hidden="true">
            ITAC
          </span>
          <span className="wr-brand-name">
            Is There A Camera
            <small>For unfamiliar rooms</small>
          </span>
        </a>

        <nav className="wr-nav" aria-label="Navigation principale">
          <a href="#methode">Méthode</a>
          <a href="#limite">Limites</a>
        </nav>

        <p className="wr-release">
          <span aria-hidden="true" />
          Desktop + iPhone · bientôt
        </p>
      </header>

      <section className="wr-hero" aria-labelledby="waiting-room-title">
        <div className="wr-hero-copy">
          <p className="wr-overline">
            <span>01</span>
            Contrôle de confidentialité pour voyageurs
          </p>

          <h1 id="waiting-room-title">
            Entrez.
            <br />
            Fermez la porte.
            <br />
            <span>Vérifiez la pièce.</span>
          </h1>

          <div className="wr-intro-row">
            <p>
              Une application desktop pour analyser le réseau en profondeur,
              accompagnée d’une app iPhone pour poursuivre le contrôle
              directement dans la pièce.
            </p>
            <a className="wr-cta" href={MAILTO}>
              <span>Tester les apps</span>
              <b aria-hidden="true">↗</b>
            </a>
          </div>

          <div className="wr-platforms" aria-label="Applications ITAC">
            <article className="wr-platform wr-platform-desktop">
              <span>01 / DESKTOP</span>
              <strong>Poste de contrôle</strong>
              <small>Analyse réseau complète depuis votre ordinateur.</small>
            </article>
            <article className="wr-platform wr-platform-iphone">
              <span>02 / IPHONE</span>
              <strong>Guide dans la pièce</strong>
              <small>Inspection mobile et vérifications sur place.</small>
            </article>
          </div>

          <p className="wr-quiet-note">
            Une adresse e-mail. Un message lorsque les premiers accès ouvriront.
          </p>
        </div>

        <aside className="wr-protocol" aria-labelledby="protocol-title">
          <header className="wr-protocol-header">
            <div className="wr-protocol-meta">
              <span>ROOM / 214</span>
              <span className="wr-protocol-live">
                <i aria-hidden="true" />
                Scène interactive
              </span>
            </div>
            <div className="wr-protocol-title-row">
              <strong id="protocol-title">Fiche de contrôle</strong>
              <span>3D / 03 POINTS</span>
            </div>
          </header>

          <div className="wr-room-viewport" data-phase={phase}>
            <Suspense fallback={<SceneFallback />}>
              <RoomScene3D
                phase={phase}
                visible={pageVisible}
                reducedMotion={reducedMotion}
                onSelectPhase={setPhase}
              />
            </Suspense>
            <div className="wr-scene-corner-label" aria-hidden="true">
              <span>3D</span>
              Glissez / explorez
            </div>
          </div>

          <div className="wr-scene-readout" aria-live="polite">
            <span>{sceneReadout.code}</span>
            <div>
              <strong>{sceneReadout.title}</strong>
              <p>{sceneReadout.description}</p>
            </div>
            {phase !== "entry" ? (
              <button type="button" onClick={() => setPhase("entry")}>
                Recentrer
              </button>
            ) : (
              <small>Vue libre</small>
            )}
          </div>

          <div
            className="wr-inspection-tabs"
            role="group"
            aria-label="Points d’inspection de la chambre">
            {INSPECTION_POINTS.map((point) => (
              <button
                key={point.id}
                className={phase === point.id ? "is-active" : ""}
                type="button"
                aria-pressed={phase === point.id}
                onClick={() => setPhase(point.id)}>
                <span>{point.code}</span>
                <strong>{point.shortTitle}</strong>
                <small>{point.hint}</small>
              </button>
            ))}
          </div>

          <footer>
            Illustration interactive
            <span>Aucun scan exécuté ici</span>
          </footer>
        </aside>
      </section>

      <section className="wr-strip" aria-label="Principes du produit">
        <p>
          <span>01</span>
          Analyse locale
        </p>
        <p>
          <span>02</span>
          Aucun flux vidéo ouvert
        </p>
        <p>
          <span>03</span>
          Aucun verdict inventé
        </p>
        <p className="wr-strip-end">ITAC / IS THERE A CAMERA</p>
      </section>

      <section className="wr-manifesto" aria-labelledby="manifesto-title">
        <p className="wr-section-index">02 / POURQUOI</p>
        <h2 id="manifesto-title">
          Le doute ne mérite ni panique, ni fausse certitude.
          <span> Il mérite une méthode.</span>
        </h2>
        <p className="wr-manifesto-note">
          Is There A Camera transforme les signaux techniques en informations lisibles,
          sans confondre appareil détecté, caméra probable et preuve d’enregistrement.
        </p>
      </section>

      <section className="wr-method" id="methode" aria-labelledby="method-title">
        <header>
          <p className="wr-section-index">03 / MÉTHODE</p>
          <h2 id="method-title">Trois couches. Une lecture claire.</h2>
          <p>
            Le téléphone rassemble ce qu’il peut réellement observer et indique
            précisément ce qui reste hors de sa portée.
          </p>
        </header>

        <div className="wr-method-grid">
          <article>
            <span className="wr-method-number">01</span>
            <div className="wr-method-glyph wr-method-glyph-network" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
            <h3>Observer</h3>
            <p>
              Analyser le réseau local depuis l’application desktop, puis
              compléter avec les signaux Bluetooth accessibles sur iPhone.
            </p>
          </article>
          <article>
            <span className="wr-method-number">02</span>
            <div className="wr-method-glyph wr-method-glyph-compare" aria-hidden="true">
              <i />
              <i />
            </div>
            <h3>Expliquer</h3>
            <p>
              Regrouper les indices, nommer les incertitudes et montrer pourquoi
              un appareil attire l’attention.
            </p>
          </article>
          <article>
            <span className="wr-method-number">03</span>
            <div className="wr-method-glyph wr-method-glyph-look" aria-hidden="true">
              <i />
            </div>
            <h3>Guider</h3>
            <p>
              Prolonger le scan par une inspection physique simple des objets et
              zones sensibles de la pièce.
            </p>
          </article>
        </div>
      </section>

      <section className="wr-limit" id="limite" aria-labelledby="limit-title">
        <div className="wr-limit-label">
          <span>!</span>
          <p>La limite fondamentale</p>
        </div>
        <div className="wr-limit-copy">
          <h2 id="limit-title">Une caméra silencieuse ne répondra à aucun scan.</h2>
          <p>
            Elle peut enregistrer hors ligne, utiliser un autre réseau ou rester
            inactive. C’est pourquoi Is There A Camera parle d’indices et de couverture,
            jamais de garantie absolue.
          </p>
        </div>
        <strong>VOIR ≠ PROUVER</strong>
      </section>

      <section className="wr-final-cta" aria-labelledby="final-cta-title">
        <p className="wr-section-index">04 / ACCÈS ANTICIPÉ</p>
        <h2 id="final-cta-title">
          Le poste de contrôle sur desktop. Le guide terrain sur iPhone.
        </h2>
        <a className="wr-cta wr-cta-inverse" href={MAILTO}>
          <span>Je veux les tester</span>
          <b aria-hidden="true">↗</b>
        </a>
      </section>

      <footer className="wr-footer">
        <span>© 2026 ITAC</span>
        <span>Is There A Camera?</span>
        <a href="mailto:san@data-san.com">san@data-san.com</a>
      </footer>
    </main>
  );
}
